import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

export type Budget = Tables<'budgets'>;
export type BudgetItem = Tables<'budget_items'> & {
  categories?: { name: string; icon: string | null } | null;
};

export function useBudgets(type?: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['budgets', user?.id, type],
    queryFn: async () => {
      let query = supabase.from('budgets').select('*').order('created_at', { ascending: false });
      if (type) query = query.eq('type', type);
      const { data, error } = await query;
      if (error) throw error;
      return data as Budget[];
    },
    enabled: !!user,
  });
}

export function useBudgetItems(budgetId?: string) {
  return useQuery({
    queryKey: ['budget_items', budgetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('budget_items')
        .select('*, categories(name, icon)')
        .eq('budget_id', budgetId!)
        .order('created_at');
      if (error) throw error;
      return data as BudgetItem[];
    },
    enabled: !!budgetId,
  });
}

export function useCreateBudget() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (budget: Omit<TablesInsert<'budgets'>, 'user_id'>) => {
      const { data, error } = await supabase
        .from('budgets')
        .insert({ ...budget, user_id: user!.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['budgets'] }),
  });
}

export function useDeleteBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('budgets').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['budgets'] }),
  });
}

export function useCreateBudgetItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (item: TablesInsert<'budget_items'>) => {
      const { data, error } = await supabase
        .from('budget_items')
        .insert(item)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['budget_items'] }),
  });
}

export function useDeleteBudgetItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('budget_items').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['budget_items'] }),
  });
}
